import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Switch,
  Select,
  Divider,
  Row,
  Col,
  Avatar,
  Modal,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  SettingOutlined,
  BellOutlined,
  SafetyOutlined,
  EditOutlined,
} from '@ant-design/icons';
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  fetchAdminSettings,
  saveAdminSettings,
  updateAdminProfile,
} from '../../services/firestoreService';

const { Option } = Select;

const AdminSettings = () => {
  const { admin, logActivity } = useAdminAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    loginAlerts: true,
    autoLogout: 30,
    twoFactorAuth: false,
  });
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);

  useEffect(() => {
    if (admin?.uid) {
      loadSettings();
    }
    logActivity('view_settings', 'Viewed settings page');

    if (admin) {
      profileForm.setFieldsValue({
        name: admin.name,
        email: admin.email,
        role: admin.role,
      });
    }
  }, [admin]);

  const loadSettings = async () => {
    try {
      const savedSettings = await fetchAdminSettings(admin.uid);
      setSettings(savedSettings);
    } catch {
      console.error('Failed to load admin settings');
    }
  };

  const handleProfileUpdate = () => {
    profileForm.validateFields().then(async (values) => {
      try {
        await updateAdminProfile(admin.uid, { name: values.name });
        message.success('Profile updated successfully');
        logActivity('update_profile', 'Updated admin profile');
        window.location.reload();
      } catch {
        message.error('Failed to update profile');
      }
    });
  };

  const handlePasswordChange = () => {
    passwordForm.validateFields().then(async (values) => {
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser?.email) {
          message.error('Not authenticated');
          return;
        }

        const credential = EmailAuthProvider.credential(
          firebaseUser.email,
          values.currentPassword
        );
        await reauthenticateWithCredential(firebaseUser, credential);
        await updatePassword(firebaseUser, values.newPassword);

        message.success('Password changed successfully');
        logActivity('change_password', 'Changed admin password');
        setIsPasswordModalVisible(false);
        passwordForm.resetFields();
      } catch (error) {
        if (error.code === 'auth/wrong-password') {
          message.error('Current password is incorrect');
        } else {
          message.error('Failed to change password');
        }
      }
    });
  };

  const handleSettingsChange = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    try {
      await saveAdminSettings(admin.uid, newSettings);
      message.success('Settings updated');
      logActivity('update_settings', `Updated setting: ${key}`);
    } catch {
      message.error('Failed to save settings');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account settings and preferences</p>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <UserOutlined className="text-cyan-500" />
                <span>Profile Information</span>
              </div>
            }
            className="shadow-sm"
          >
            <div className="flex flex-col items-center mb-6">
              <Avatar
                size={80}
                className="bg-gradient-to-br from-cyan-400 to-cyan-600 text-2xl font-bold"
              >
                {admin?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <h3 className="text-lg font-semibold mt-3">{admin?.name}</h3>
              <p className="text-gray-500 text-sm capitalize">{admin?.role}</p>
            </div>

            <Form form={profileForm} layout="vertical">
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Admin Name" />
              </Form.Item>

              <Form.Item label="Email" name="email">
                <Input prefix={<MailOutlined />} disabled />
              </Form.Item>

              <Form.Item label="Role" name="role">
                <Select disabled>
                  <Option value="superadmin">Super Admin</Option>
                  <Option value="admin">Admin</Option>
                </Select>
              </Form.Item>

              <Button
                type="primary"
                onClick={handleProfileUpdate}
                icon={<EditOutlined />}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
              >
                Update Profile
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <SafetyOutlined className="text-pink-500" />
                <span>Security</span>
              </div>
            }
            className="shadow-sm"
          >
            <div className="space-y-4">
              <Button
                type="default"
                icon={<LockOutlined />}
                onClick={() => setIsPasswordModalVisible(true)}
                className="w-full"
                size="large"
              >
                Change Password
              </Button>

              <Divider />

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-xs text-gray-500">Add an extra layer of security</p>
                </div>
                <Switch
                  checked={settings.twoFactorAuth}
                  onChange={(checked) => handleSettingsChange('twoFactorAuth', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Auto Logout</p>
                  <p className="text-xs text-gray-500">After {settings.autoLogout} minutes of inactivity</p>
                </div>
                <Select
                  value={settings.autoLogout}
                  onChange={(value) => handleSettingsChange('autoLogout', value)}
                  style={{ width: 100 }}
                >
                  <Option value={15}>15 min</Option>
                  <Option value={30}>30 min</Option>
                  <Option value={60}>60 min</Option>
                  <Option value={120}>120 min</Option>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Login Alerts</p>
                  <p className="text-xs text-gray-500">Get notified of new logins</p>
                </div>
                <Switch
                  checked={settings.loginAlerts}
                  onChange={(checked) => handleSettingsChange('loginAlerts', checked)}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <BellOutlined className="text-yellow-500" />
                <span>Notifications</span>
              </div>
            }
            className="shadow-sm"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-xs text-gray-500">Receive email updates</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(checked) => handleSettingsChange('emailNotifications', checked)}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <SettingOutlined className="text-blue-500" />
                <span>System Preferences</span>
              </div>
            }
            className="shadow-sm"
          >
            <div className="space-y-4">
              <div>
                <p className="font-medium mb-2">Date Format</p>
                <Select defaultValue="MM/DD/YYYY" className="w-full">
                  <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                  <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                  <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                </Select>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Change Password"
        open={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        onOk={handlePasswordChange}
        okText="Change Password"
        okButtonProps={{ className: 'bg-cyan-500 hover:bg-cyan-600' }}
      >
        <Form form={passwordForm} layout="vertical" className="mt-4">
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: 'Please enter current password' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminSettings;
